import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import Script from 'next/script'
import type { ReactElement } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { z } from 'zod'

import { ONE_COLUMN_WIDTH } from '@/constants/content'
import { getLocaleParts } from '@/utils/locale'
import type { PortableTextTypeComponentProps } from '@portabletext/react'

const BUSINESS_UNIT_ID = '5e24b4aea425ec0001d447d6'
const MINI_SYTLE_TEMPLATE_ID = '53aa8807dec7e10d38f59f32'
const MICRO_COMBO_TEMPLATE_ID = '5419b6ffb0d04a076446a9af'
const TRUSTPILOT_MAX_WIDTH = 255
const TRUSTPILOT_MAX_HEIGHT = 40
const MICRO_STAR_TEMPLATE_ID = '5419b732fbfb950b10de65e5'

const trustPilotWidget = z.object({
  _type: z.literal('trustPilotWidget'),
  style: z.enum(['microCombo', 'mini', 'microStar']).optional(),
  templateId: z.string().optional(),
  theme: z.enum(['dark', 'light']).optional(),
})

type TrustPilotWidgetSchema = z.infer<typeof trustPilotWidget>

const getTemplateId = (style?: TrustPilotWidgetSchema['style']) => {
  switch (style) {
    case 'microStar':
      return MICRO_STAR_TEMPLATE_ID
    case 'microCombo':
      return MICRO_COMBO_TEMPLATE_ID

    case 'mini':
    default:
      return MINI_SYTLE_TEMPLATE_ID
  }
}

enum Status {
  Loading,
  Success,
  Error,
}

export const TrustpilotWidget = (props: {
  node: Pick<PortableTextTypeComponentProps<unknown>, 'value'>
  width: number
}): ReactElement => {
  const value = useMemo(() => trustPilotWidget.parse(props.node.value), [props.node.value])
  const [scriptStatus, setScriptStatus] = useState(Status.Loading)
  const ref = useRef<HTMLDivElement>(null)
  const { locale } = useRouter()
  const [lang, market] = getLocaleParts(locale)

  useEffect(() => {
    if (scriptStatus === Status.Success) {
      window.Trustpilot?.loadFromElement(ref.current, true)
    }
  }, [scriptStatus])

  const templateId = value.templateId ?? getTemplateId(value.style)
  return (
    <>
      <Script
        src="//widget.trustpilot.com/bootstrap/v5/tp.widget.sync.bootstrap.min.js"
        onReady={() => {
          setScriptStatus(Status.Success)
        }}
        onError={() => setScriptStatus(Status.Error)}
      />
      <div
        className="flex flex-col overflow-hidden"
        style={{
          width: props.width === ONE_COLUMN_WIDTH ? undefined : TRUSTPILOT_MAX_WIDTH,
          height: templateId !== MINI_SYTLE_TEMPLATE_ID ? TRUSTPILOT_MAX_HEIGHT : undefined,
        }}
      >
        <div>
          <div
            ref={ref}
            className="trustpilot-widget"
            data-locale={`${lang}-${market}`}
            data-template-id={templateId}
            data-businessunit-id={BUSINESS_UNIT_ID}
            data-style-height="100%"
            data-style-width="100%"
            data-theme={value.theme ?? 'light'}
          >
            <Link href="https://www.trustpilot.com/review/www.hellogetsafe.com" rel="noopener">
              {scriptStatus !== Status.Error ? (
                'Trustpilot'
              ) : (
                <Image
                  src="/assets/images/svg/tp-logo-big-dark-center.svg"
                  width={251}
                  height={108}
                  loader={({ src }) => src}
                  unoptimized
                  alt="Trustpilot light icon"
                />
              )}
            </Link>
          </div>
        </div>
      </div>
    </>
  )
}
